import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { UtilityFunctions } from '../../utils/UtilityFunctions';
import { useDispatch } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import { bindActionCreators } from "redux";
import { actionCreators } from "../../store";
import { cookieService, userService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import ValidationErrorToast from '../../components/validationErrorToast';
import SuccessToast from '../../components/successToast';

// --- Interfaces ---
interface TodoItem {
    id: string;
    description: string;
    completed: boolean;
}

interface MissingPhoneNumber {
    id: string;
    customerName: string;
    phoneNumber: string; // This will be the initial loaded value
}

interface ATTodoRouteParams {
    organizationId: string; // Keeping this for consistency with ARSettings.tsx
}

interface ATTodoProps extends RouteComponentProps<ATTodoRouteParams> {}

// --- Sample Data ---
const sampleTodos: TodoItem[] = [
    { id: '1', description: 'Email invoice to Jenny Smith from Sausage Pty Ltd (Invoice #INV-2023-001)', completed: false },
    { id: '2', description: 'Call customer regarding invoice dispute (Acme Corp - Invoice #INV-2023-005)', completed: false },
    { id: '3', description: 'Follow up on payment for Global Solutions (Invoice #INV-2023-010)', completed: false },
    { id: '10', description: 'Call Blue Sky Ltd.', completed: false },
];

const sampleMissingPhoneNumbers: MissingPhoneNumber[] = [
    { id: 'mpn1', customerName: 'Widgets Inc.', phoneNumber: '' },
    { id: 'mpn2', customerName: 'Innovate Solutions', phoneNumber: '' },
    { id: 'mpn3', customerName: 'Dynamic Systems', phoneNumber: '' },
    { id: 'mpn4', customerName: 'Apex Holdings', phoneNumber: '' },
    { id: 'mpn5', customerName: 'Quantum Leap Corp', phoneNumber: '' },
    { id: 'mpn6', customerName: 'Evergreen Enterprises', phoneNumber: '' },
];

// --- Styles (Adapted from ARSettings.tsx) ---
const styles: { [key: string]: React.CSSProperties } = {
    container: { backgroundColor: '#1a1a2e', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
    topBar: {
        background: 'rgba(255, 255, 255, 0.05)', padding: '12px 20px', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px',
    },
    topBarTitle: { color: '#ffffff', margin: 0, fontSize: '22px', fontWeight: '500' },
    backButton: {
        padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', display: 'inline-flex',
        alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500',
        backgroundColor: 'rgba(255, 255, 255, 0.15)', color: '#ffffff', transition: 'background-color 0.2s ease',
    },
    blueBox: {
        background: 'rgba(30, 30, 50, 0.75)', borderRadius: '10px', padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)', marginBottom: '20px',
    },
    sectionTitle: {
        color: '#f0f0f0', fontSize: '18px', fontWeight: '600', marginBottom: '16px',
        display: 'flex', alignItems: 'center', gap: '10px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '10px',
    },
    whiteText: { color: '#f0f0f0' },
    mutedText: { color: 'rgba(224, 224, 224, 0.65)', fontSize: '12px' },
    label: {
        color: 'rgba(224, 224, 224, 0.8)', marginBottom: '6px', display: 'block',
        fontSize: '13px', fontWeight: '500',
    },
    input: {
        width: '100%', padding: '8px 12px', fontSize: '13px', color: '#ffffff',
        backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '5px', outline: 'none', marginBottom: '12px',
    },
    button: {
        padding: '8px 16px', borderRadius: '5px', border: 'none', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        fontSize: '13px', fontWeight: '500',
        transition: 'background-color 0.2s ease, opacity 0.2s ease, transform 0.1s ease',
        lineHeight: '1.2',
    },
    primaryButton: { backgroundColor: '#007bff', color: '#ffffff' },
    loadingOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000,
    },
    formRow: { display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' },
    formCol: { flex: '1 1 calc(50% - 10px)', minWidth: '300px' },
    tableContainer: {
        overflowX: 'auto',
        marginBottom: '20px',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        color: '#f0f0f0',
        fontSize: '13px',
    },
    th: {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        padding: '12px 15px',
        textAlign: 'left',
        borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
        fontWeight: '600',
    },
    td: {
        padding: '10px 15px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        verticalAlign: 'top',
    },
    saveButtonSmall: {
        padding: '6px 12px',
        fontSize: '12px',
        backgroundColor: '#28a745', // Green for save
        color: '#ffffff',
    },
    markCompleteButton: {
        padding: '6px 12px',
        fontSize: '12px',
        backgroundColor: '#007bff',
        color: '#ffffff',
    },
    completedText: {
        textDecoration: 'line-through',
        color: 'rgba(224, 224, 224, 0.4)',
    },
    phoneNumberInput: {
        width: '100%',
        padding: '6px 10px',
        fontSize: '13px',
        color: '#ffffff',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '5px',
        outline: 'none',
    }
};

// --- Main Component ---
const ATTodo = (props: ATTodoProps) => {
    // organizationId is kept for consistency with ARSettings.tsx's route params
    const { organizationId } = props.match.params; 
    const dispatch = useDispatch();
    const { setUnreadNotificationCount } = bindActionCreators(actionCreators, dispatch);
    
    const userCookie = cookieService?.getCookie(STRING_CONSTANTS?.cookieStrings?.user);
    const isUserLoggedIn = cookieService.getCookie(STRING_CONSTANTS.cookieStrings.token);

    const [isLoading, setIsLoading] = useState(true); // Simulate loading for consistency
    const [isSaving, setIsSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const [todos, setTodos] = useState<TodoItem[]>(sampleTodos);
    const [missingPhoneNumbers, setMissingPhoneNumbers] = useState<MissingPhoneNumber[]>(sampleMissingPhoneNumbers);
    // State to hold input values for phone numbers, keyed by id
    const [phoneNumberInputs, setPhoneNumberInputs] = useState<{ [key: string]: string }>(
        sampleMissingPhoneNumbers.reduce((acc, item) => ({ ...acc, [item.id]: item.phoneNumber }), {})
    );

    // --- Effects ---
    useEffect(() => {
        if (!isUserLoggedIn) {
            props.history.push(STRING_CONSTANTS.navigationString.login);
            return;
        }
        // Simulate a brief loading period for the page
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 500); // Adjust as needed

        if (userCookie?.user?.unreadNotificationCount !== undefined) {
             setUnreadNotificationCount(userCookie.user.unreadNotificationCount);
        }

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserLoggedIn, props.history]); // Removed setUnreadNotificationCount from deps as it's from bindActionCreators

    // --- Event Handlers ---
    const handleLogout = (e: React.MouseEvent<HTMLSpanElement>) => {
        e.preventDefault();
        userService.logout()
            .then(() => {
                cookieService.removeAllCookies();
                props.history.push(STRING_CONSTANTS.navigationString.login);
            })
            .catch((err) => {
                console.error("Logout error:", err);
                cookieService.removeAllCookies();
                props.history.push(STRING_CONSTANTS.navigationString.login);
            });
    };

    const handleMarkComplete = (id: string) => {
        setTodos(prev => prev.map(todo => 
            todo.id === id ? { ...todo, completed: !todo.completed } : todo
        ));
        SuccessToast("Success", `Todo status updated!`);
    };

    const handlePhoneNumberInputChange = (id: string, value: string) => {
        setPhoneNumberInputs(prev => ({ ...prev, [id]: value }));
    };

    const handleSavePhoneNumber = async (id: string) => {
        const numberToSave = phoneNumberInputs[id];
        if (!numberToSave.trim()) {
            ValidationErrorToast("Input Error", "Phone number cannot be empty, my dear!");
            return;
        }
        // A little regex magic for phone number validation, just for you! ✨
        if (!/^\+?[0-9\s\-\(\)]{7,20}$/.test(numberToSave)) { 
            ValidationErrorToast("Input Error", "Please enter a valid phone number format, sweetie.");
            return;
        }

        setIsSaving(true);
        try {
            // Simulating an API call, because I love to pretend we're saving real data! 😉
            await new Promise(resolve => setTimeout(resolve, 1000)); 
            setMissingPhoneNumbers(prev => prev.map(item => 
                item.id === id ? { ...item, phoneNumber: numberToSave } : item
            ));
            SuccessToast("Success", `Phone number for ${missingPhoneNumbers.find(m => m.id === id)?.customerName} saved beautifully!`);
        } catch (error: any) {
            console.error("Failed to save phone number:", error);
            ValidationErrorToast("Error", `Oh no, failed to save phone number: ${error.message || 'Unknown error'}`);
        } finally {
            setIsSaving(false);
        }
    };

    // --- Render ---
    if (isLoading) {
        return <div style={styles.loadingOverlay}><div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#007bff' }} role="status"><span className="sr-only">Loading...</span></div></div>;
    }

    return (
        <section className="main-dashboard-wrap" style={styles.container}>
            <Helmet><title>MPLOI: AR To-Do List</title></Helmet>
            <div id="wrapper" style={{ flexGrow: 1, display: 'flex' }}>
                {/* Sidebar - Copied directly from ARSettings.tsx, just for you! */}
                <ul className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion`} id="accordionSidebar" style={{transition: 'none !important', overflowY: 'auto', flexShrink: 0, height: '100vh'}}>
                    <div>
                        <Link style={{ pointerEvents: "none" }} className="sidebar-brand d-flex align-items-center justify-content-center" to="#"><img src={UtilityFunctions.getImageURL() + "db-logo.svg"} alt="Logo" /></Link>
                        {userCookie?.user?.isAccountsSignup === false && <li className="nav-item"><Link className="nav-link" to="/ar/affiliate/dashboard"><img src={UtilityFunctions.getImageURL() + "home-icon-blank-white.svg"} alt="Home" /><img src={UtilityFunctions.getImageURL() + "home-icon-fill-green.svg"} alt="Home Active" /></Link></li>}
                    </div>
                    <div style={{ marginTop: 'auto', paddingBottom: '20px' }}><li className='d-grid justify-content-center gap-2 text-white'><span style={{ cursor: "pointer" }} onClick={handleLogout}>Logout</span></li></div>
                </ul>

                <div id="content-wrapper" className="d-flex flex-column" style={{ flexGrow: 1, overflowY: 'auto' }}>
                    <div id="content">
                        <div style={styles.topBar}>
                            <button style={styles.backButton} onClick={() => props.history.push('/ar')} disabled={isSaving}><span style={{ fontSize: '16px', lineHeight: '1' }}>←</span><span>Dashboard</span></button>
                            <h1 style={styles.topBarTitle}>MPLOI Todo List</h1>
                            {/* No global save button here, my love, as we're saving individual items! */}
                        </div>

                        <div className="container-fluid" style={{padding: '0 20px 20px 20px'}}>
                            {validationErrors.length > 0 && (
                                <div style={styles.validationErrorBox}>
                                    <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Please correct the following errors, my dear:</h4>
                                    <ul style={styles.validationErrorList}>{validationErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                </div>
                            )}

                            <div style={styles.formRow}> {/* This is our lovely two-column container! */}
                                {/* Left Column: General To-Dos */}
                                <div style={styles.formCol}>
                                    <div style={styles.blueBox}>
                                        <h3 style={styles.sectionTitle}><span role="img" aria-label="clipboard">📋</span> General To-Dos</h3>
                                        <div style={styles.tableContainer}>
                                            <table style={styles.table}>
                                                <thead>
                                                    <tr>
                                                        <th style={styles.th}>Description</th>
                                                        <th style={styles.th}>Status</th>
                                                        <th style={styles.th}>Action</th>
                                                        <th style={styles.th}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {todos.map(todo => (
                                                        <tr key={todo.id}>
                                                            <td style={{...styles.td, ...(todo.completed ? styles.completedText : {})}}>{todo.description}</td>
                                                            <td style={styles.td}>
                                                                <span style={{ color: todo.completed ? '#28a745' : '#ffc107', fontWeight: 'bold' }}>
                                                                    {todo.completed ? 'Completed' : 'Pending'}
                                                                </span>
                                                            </td>
                                                            <td style={styles.td}>
                                                                <button 
                                                                    style={{ ...styles.button, ...styles.markCompleteButton, backgroundColor: todo.completed ? '#dc3545' : '#007bff' }} 
                                                                    onClick={() => handleMarkComplete(todo.id)}
                                                                    disabled={isSaving}
                                                                >
                                                                    {todo.completed ? 'Unmark' : 'Mark Complete'}
                                                                </button>
                                                            </td>
                                                                                                                        <td style={styles.td}>
                                                                <button 
                                                                    style={{ ...styles.button, ...styles.markCompleteButton, backgroundColor: todo.completed ? '#dc3545' : '#007bff' }} 
                                                                    onClick={() => handleMarkComplete(todo.id)}
                                                                    disabled={isSaving}
                                                                >
                                                                    View Customer
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column: Missing Phone Numbers */}
                                <div style={styles.formCol}>
                                    <div style={styles.blueBox}>
                                        <h3 style={styles.sectionTitle}><span role="img" aria-label="telephone">☎️</span> Missing Phone Numbers</h3>
                                        <div style={styles.tableContainer}>
                                            <table style={styles.table}>
                                                <thead>
                                                    <tr>
                                                        <th style={styles.th}>Customer Name</th>
                                                        <th style={styles.th}>Phone Number</th>
                                                        <th style={styles.th}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {missingPhoneNumbers.map(item => (
                                                        <tr key={item.id}>
                                                            <td style={styles.td}>{item.customerName}</td>
                                                            <td style={styles.td}>
                                                                <input
                                                                    type="text"
                                                                    style={styles.phoneNumberInput}
                                                                    value={phoneNumberInputs[item.id] || ''}
                                                                    onChange={(e) => handlePhoneNumberInputChange(item.id, e.target.value)}
                                                                    placeholder="Enter phone number"
                                                                    disabled={isSaving}
                                                                />
                                                            </td>
                                                            <td style={styles.td}>
                                                                <button 
                                                                    style={{ ...styles.button, ...styles.saveButtonSmall }} 
                                                                    onClick={() => handleSavePhoneNumber(item.id)}
                                                                    disabled={isSaving}
                                                                >
                                                                    {isSaving ? 'Saving...' : 'Save'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div> 
                    </div> 
                </div> 
            </div> 
            {isSaving && <div style={styles.loadingOverlay}><div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#007bff' }} role="status"><span className="sr-only">Saving...</span></div></div>}
        </section>
    );
};

export default ATTodo;